// Keycloak configuration and helper functions

interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

export interface KeycloakTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
}

interface KeycloakRawTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in?: number;
  id_token?: string;
}

const keycloakConfig: KeycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'veritas',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'veritas-gui',
};

/**
 * Generates a random string for PKCE code verifier
 */
const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

/**
 * Generates code challenge from verifier for PKCE
 */
const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
};

/**
 * Base64 URL encode
 */
const base64UrlEncode = (array: Uint8Array): string => {
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Initiates Keycloak authentication flow for a specific identity provider
 * @param provider - The identity provider (google, microsoft, apple)
 */
export const loginWithKeycloak = async (provider: 'google' | 'microsoft' | 'apple') => {
  const { url, realm, clientId } = keycloakConfig;
  const redirectUri = `${window.location.origin}/oauth/callback`;
  
  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const state = crypto.randomUUID();
  sessionStorage.setItem(`pkce_verifier_${state}`, codeVerifier);
  
  // Build the Keycloak authorization URL with identity provider hint
  // Using kc_idp_hint bypasses the Keycloak login page and goes directly to the IdP
  const authUrl = new URL(`${url}/realms/${realm}/protocol/openid-connect/auth`);
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', 'openid profile email');
  authUrl.searchParams.set('kc_idp_hint', provider);
  authUrl.searchParams.set('state', state);
  authUrl.searchParams.set('code_challenge', codeChallenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');
  
  // Redirect to Keycloak (which will immediately redirect to the IdP)
  window.location.href = authUrl.toString();
};

const KC_ID_TOKEN_KEY = 'kc_id_token';
export const KC_TOKEN_EXPIRY_KEY = 'kc_token_expiry';

/**
 * Handles the OAuth callback from Keycloak.
 * Returns the full token response and stores the access token expiry
 * in sessionStorage so AuthContext can schedule proactive refresh.
 */
export const handleKeycloakCallback = async (
  code: string,
  state?: string | null,
): Promise<KeycloakTokenResponse> => {
  const { url, realm, clientId } = keycloakConfig;
  const redirectUri = `${window.location.origin}/oauth/callback`;

  if (!state) {
    throw new Error('OAuth state not found');
  }

  const verifierKey = `pkce_verifier_${state}`;
  const codeVerifier = sessionStorage.getItem(verifierKey);

  if (!codeVerifier) {
    throw new Error('Code verifier not found');
  }

  sessionStorage.removeItem(verifierKey);

  const tokenUrl = `${url}/realms/${realm}/protocol/openid-connect/token`;

  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code: code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error_description || 'Failed to exchange authorization code');
  }

  const data = await response.json() as KeycloakRawTokenResponse;

  if (!data.access_token || !data.refresh_token || !data.expires_in) {
    throw new Error('Incomplete token response from Keycloak: missing required fields');
  }

  if (data.id_token) {
    sessionStorage.setItem(KC_ID_TOKEN_KEY, data.id_token);
  }

  // Store expiry timestamp so AuthContext can schedule proactive refresh
  sessionStorage.setItem(KC_TOKEN_EXPIRY_KEY, String(Date.now() + data.expires_in * 1000));

  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
    refreshExpiresIn: data.refresh_expires_in ?? 1800,
  };
};

/**
 * Logs out from Keycloak.
 * Uses the stored ID token so Keycloak skips the logout confirmation screen.
 */
export const logoutFromKeycloak = () => {
  const { url, realm, clientId } = keycloakConfig;
  const redirectUri = window.location.origin;
  
  const logoutUrl = new URL(`${url}/realms/${realm}/protocol/openid-connect/logout`);
  logoutUrl.searchParams.set('client_id', clientId);
  logoutUrl.searchParams.set('post_logout_redirect_uri', redirectUri);
  
  const idToken = sessionStorage.getItem(KC_ID_TOKEN_KEY);
  if (idToken) {
    logoutUrl.searchParams.set('id_token_hint', idToken);
    sessionStorage.removeItem(KC_ID_TOKEN_KEY);
  }
  
  window.location.href = logoutUrl.toString();
};

export { keycloakConfig };
